"use client"

/*
 * DEV HANDOFF - route-settings.tsx
 *
 * ROUTE SETTINGS, THE COMPLEX UI'S EXTRA LEVEL - the redesign of legacy's
 * `views/Share/RouteSettings.jsx`.
 *
 * WHY THIS IS ITS OWN PROJECT. dlvr.it has two UIs, chosen per account by
 * `app.flags.routeViewType` against `code/classes/types.php:960`:
 *
 *   ROUTE_VIEW_ADVANCED = 0   "for the old timers (routes with multiple
 *                              inputs/outputs)"        -> COMPLEX
 *   ROUTE_VIEW_SIMPLE   = 1   "simple style for the 2.0 users (routes with
 *                              one input, n outputs)"  -> BASIC
 *
 *   COMPLEX   route -> feeds (many) -> destinations     3 settings levels
 *   BASIC     feed -> destinations                      2 settings levels
 *
 * Complex reaches the SAME feed and destination settings the other screens
 * already cover; what it adds is a settings level above them, for the route
 * that groups several feeds. That level is this screen and nothing else, which
 * is why it is one page rather than a parallel set.
 *
 * FOUR TABS, NO PRUNING AND NO DELETE. `RouteSettings.jsx:118` declares
 * Detail / Filters / Tracking / Shorteners unconditionally - unlike the feed
 * and destination strips, nothing is hidden per source or per network. Delete
 * lives on the route row in the list, not in here, so this screen has a
 * save / cancel footer and no danger zone.
 *
 * Wears the same shell as the other three settings screens:
 *   settings-page.tsx        the shell
 *   settings-form.tsx        the divided settings list
 *   route-settings.data.ts   the field spec - MOCK DATA, does not ship
 *
 * OPEN: WHAT THIS LEVEL IS CALLED. Legacy says "route", and that is used here
 * because it is the word the screen and the URL already use. But the basic UI
 * calls a feed-plus-destinations an **automation**, and in complex it is the
 * ROUTE that plays that part while feeds are its sources - so the two UIs
 * disagree about what "automation" names, and `feed-settings.tsx` is already
 * titled "Automation settings". Naming this one is a product decision, not a
 * transcription: see LEGACY-QUESTIONS.
 *
 * WIRING (production owns all of it):
 * - `RouteSettings.jsx` keeps state, the dirty diff, save / cancel and the
 *   `ErrorModule` plumbing; only the panel bodies are replaced. Its four
 *   panels become the four sections of `sections`.
 * - Every field carries its legacy `data-field` dot path in
 *   `SettingsField.name`, all of them `route.*`.
 * - The Analytics textarea is disabled by the tracking toggle, which legacy
 *   drives through `defaultToggle` writing `route.urlAppend` to null. Two
 *   fields share that one dot path on purpose.
 * - Shorteners is one select over two columns: `route.saID` for a service,
 *   `route.paID` for a custom domain (options keyed `domain-<id>`). Split it
 *   back out on save.
 */

import * as React from "react"

import { History } from "lucide-react"

import { type SettingsSection } from "@/shadcn/components/settings/settings-form"
import {
  SettingsPage,
  type SettingsPageProps,
} from "@/shadcn/components/settings/settings-page"

type RouteSettingsProps = Partial<SettingsPageProps> & {
  /**
   * REQUIRED HERE, unlike the preview kit, where it defaults to a mock
   * fixture. Prod builds this from the stored record; there is no fixture to
   * fall back to. See `settings-vocab.ts` for why the split exists.
   */
  sections: SettingsSection[]
  /** The route being edited. Shown in the meta line under the title. */
  routeName?: string
  /** How many sources this route pulls from - the reason the level exists. */
  sourceCount?: number
}

export default function RouteSettings({
  sections,
  routeName = "My New Route",
  sourceCount = 1,
  title,
  meta,
  backLabel,
  note,
  actions,
  /* Delete is on the route row in the list, not in its settings. */
  showDelete = false,
  ...props
}: RouteSettingsProps) {
  return (
    <SettingsPage
      sections={sections}
      /*
       * The name leads and the settings noun sits under it (Emily,
       * 2026-10-01): the reader arrives knowing which route they opened, so
       * that is what the big line confirms. Same type as before, swapped.
       */
      title={title ?? routeName}
      /*
       * The source count is the whole point of this level: a route exists to
       * hold more than one source, and these settings apply across all of them.
       * Legacy shows the count on the route row but not here, so the reader
       * arriving from the row has to remember it.
       *
       * "source", not "feed", per I6b: complex keeps the word it already uses
       * in the redesign rather than adopting legacy's "feed".
       */
      meta={
        meta ??
        `Route settings · ${sourceCount} ${sourceCount === 1 ? "source" : "sources"}`
      }
      backLabel={backLabel ?? routeName}
      /*
       * The one thing this screen has to say that no label can: these settings
       * sit ABOVE the per-feed ones, and both apply. Filters especially - a
       * route filter and a feed filter are separate gates on the same item.
       */
      note={
        /*
         * `undefined` MEANS "NOT SPECIFIED", `null` MEANS "NONE". `??` cannot
         * tell the two apart, and a dismissal passes null — so under `??` the
         * note came straight back. `SettingsPageProps` states the contract;
         * this is the explicit check that keeps it.
         */
        note !== undefined
          ? note
          : "These settings apply to every source on this route. Each source has its own settings too."
      }
      actions={actions ?? [{ label: "Error log", icon: <History /> }]}
      showDelete={showDelete}
      {...props}
    />
  )
}
